import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { fila } from '../components/movimiento';
import { api, textoUbicacion, type PalletEliminado } from '../api';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { useEsTactil } from '../lib/useMedia';
import {
  Buscador,
  Cargando,
  Encabezado,
  filtrar,
  Icono,
  Paginador,
  PanelError,
  useConfirmar,
  usePaginado,
  Vacio,
  type Pregunta,
} from '../components/ui';
import { useSesion } from '../lib/sesion';
import { aviso, botonSecundario, c, encabezadoTabla, enlace, filaTabla, miles, mono, tarjeta, HUSO } from '../theme';

const cuando = (iso: string) =>
  new Date(iso).toLocaleString('es-AR', { timeZone: HUSO, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const contenedor = (e: PalletEliminado) =>
  e.contenedor ? `${e.contenedor}${e.contenedorNumero != null ? ` · N° ${e.contenedorNumero}` : ''}` : 'Sin contenedor';

/** El código lleva a la ficha del contenedor si hay a dónde ir; el N° queda como texto. */
function Contenedor({ e, onVer }: { e: PalletEliminado; onVer?: (ct: string) => void }) {
  const puede = useSesion().vistas.includes('contenedor');
  if (!e.contenedor || !onVer || !puede) return <>{contenedor(e)}</>;
  return (
    <>
      <button type="button" onClick={() => onVer(e.contenedor!)} title="Ver la ficha del contenedor" style={{ ...enlace, fontSize: 'inherit' }}>
        {e.contenedor}
      </button>
      {e.contenedorNumero != null ? ` · N° ${e.contenedorNumero}` : ''}
    </>
  );
}

const ubicacion = (e: PalletEliminado) => (e.posicion ? textoUbicacion(e.posicion, e.nivel) : 'Sin ubicación');

/** Lo que se pregunta antes de restablecer: qué vuelve y, sobre todo, qué no. */
export const preguntaRestablecer = (e: PalletEliminado): Pregunta => ({
  titulo: `¿Restablecer ${e.pallet}?`,
  detalle:
    `Vuelve al sistema como estaba al eliminarlo, con lo que era de RDM arriba` +
    (e.posicion ? `, a ${ubicacion(e)} si sigue libre (si no, sin ubicar).` : ', sin ubicar.') +
    ' Lo que ya se había entregado no vuelve, ni las salidas que se borraron con él.',
  boton: 'Restablecer el pallet',
});

export const textoRestablecido = (r: { pallet: string; piezas: number; ubicacion: string | null }) =>
  `${r.pallet} volvió con ${miles(r.piezas)} piezas${r.ubicacion ? ` a ${r.ubicacion}` : ', sin ubicar: falta ubicarlo'}.`;

function BotonRestablecer({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} style={{ ...botonSecundario, minHeight: 36, padding: '6px 12px', fontSize: 13 }}>
      <Icono nombre="undo" size={17} />
      Restablecer
    </button>
  );
}

/**
 * Los pallets que un admin eliminó del sistema desde el escáner, con lo que
 * tenían arriba: de qué contenedor eran, qué productos y dónde estaban. Es el
 * registro para decidir después qué hacer con esa mercadería.
 */
export default function PalletsEliminados({ onVerContenedor }: { onVerContenedor: (ct: string) => void }) {
  const { datos, cargando, error, recargar } = useApi(() => api.palletsEliminados(), [], VIVO.normal);
  const [busqueda, setBusqueda] = useState('');
  const celular = useEsTactil(); // la tabla pide ~1000 px: en tablet (768) se apretaba y partía los códigos

  const encontrados = filtrar(datos ?? [], busqueda, (e) => [
    e.pallet,
    e.contenedor,
    e.motivo,
    e.eliminadoPor,
    e.posicion,
    ...e.items.flatMap((i) => [i.articulo, i.descripcion, i.numeroParte, i.lote]),
  ]);
  const pagina = usePaginado(encontrados);
  const [confirmar, dialogo] = useConfirmar();
  const [hecho, setHecho] = useState('');
  const [fallo, setFallo] = useState('');

  async function restablecer(e: PalletEliminado) {
    if (!(await confirmar(preguntaRestablecer(e)))) return;
    setHecho('');
    setFallo('');
    try {
      setHecho(textoRestablecido(await api.restablecerEliminado(e.id)));
      recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }
  const alRestablecer = (e: PalletEliminado) => (e.restablecible ? () => void restablecer(e) : undefined);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Encabezado
        bajada="Lo que se borró del sistema desde el escáner, con lo que tenía arriba y el motivo."
      />
      {hecho && <div role="status" style={aviso('ok')}>{hecho}</div>}
      {fallo && <div role="alert" style={aviso('err')}>{fallo}</div>}

      {error ? (
        <PanelError mensaje={error} onReintentar={recargar} />
      ) : cargando && !datos ? (
        <Cargando />
      ) : datos && datos.length === 0 ? (
        <div style={tarjeta}>
          <Vacio
            icono="delete_history"
            titulo="Todavía no se eliminó ningún pallet."
            detalle="Un admin lo hace escaneando la etiqueta del pallet: aparece la opción «Eliminar del sistema»."
          />
        </div>
      ) : (
        datos && (
          <>
            <div className="flex"><Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Pallet, producto, contenedor o motivo" /></div>

            <div style={{ ...tarjeta, overflow: 'hidden' }}>
              {encontrados.length === 0 && (
                <Vacio icono="search" titulo="Sin resultados" detalle={`Ningún pallet coincide con «${busqueda}».`} />
              )}
              {celular ? (
                <AnimatePresence initial={false}>
                  {pagina.visibles.map((e, i) => (
                    <TarjetaEliminado key={e.id} e={e} onVerContenedor={onVerContenedor} indice={i} onRestablecer={alRestablecer(e)} />
                  ))}
                </AnimatePresence>
              ) : (
                <div className="wms-scroll-x">
                  <div style={{ position: 'relative' }}>
                    <div style={encabezadoTabla}>
                      <span style={{ width: 130 }}>Eliminado</span>
                      <span style={{ width: 120 }}>Pallet</span>
                      <span style={{ width: 150 }}>Contenedor</span>
                      <span style={{ flex: 2, minWidth: 180 }}>Producto</span>
                      <span style={{ width: 70, textAlign: 'right' }}>Piezas</span>
                      <span style={{ width: 120 }}>Ubicación</span>
                      <span style={{ width: 130 }}>Estado</span>
                      <span style={{ flex: 2, minWidth: 160 }}>Motivo</span>
                      <span style={{ width: 130 }} />
                    </div>
                    <AnimatePresence mode="popLayout" initial={false}>
                    {pagina.visibles.map((e, i) => (
                      <motion.div key={e.id} layout="position" {...fila(i)} style={{ ...filaTabla, alignItems: 'flex-start' }}>
                        <span style={{ width: 130, display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                          <span style={{ fontSize: 13 }}>{cuando(e.eliminadoEn)}</span>
                          <span style={{ fontSize: 12, color: c.texto3 }}>{e.eliminadoPor}</span>
                        </span>
                        <span style={{ width: 120, fontFamily: mono, fontSize: 13, fontWeight: 500 }}>{e.pallet}</span>
                        <span style={{ width: 150, fontSize: 13, color: c.texto2 }}><Contenedor e={e} onVer={onVerContenedor} /></span>
                        <span style={{ flex: 2, minWidth: 180 }}>
                          <Productos e={e} />
                        </span>
                        <span style={{ width: 70, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                          {e.piezas != null ? miles(e.piezas) : '—'}
                        </span>
                        <span style={{ width: 120, fontFamily: mono, fontSize: 12.5, color: c.texto2 }}>{ubicacion(e)}</span>
                        <span style={{ width: 130, fontSize: 13, color: c.texto2 }}>
                          {[e.estado, e.tipo].filter(Boolean).join(' · ')}
                        </span>
                        <span style={{ flex: 2, minWidth: 160, fontSize: 13, textWrap: 'pretty' }}>{e.motivo}</span>
                        <span style={{ width: 130, display: 'flex', justifyContent: 'flex-end' }}>
                          {e.restablecible && <BotonRestablecer onClick={() => void restablecer(e)} />}
                        </span>
                      </motion.div>
                    ))}
                    </AnimatePresence>
                  </div>
                </div>
              )}
              <Paginador
                pagina={pagina.pagina}
                paginas={pagina.paginas}
                total={pagina.total}
                desde={pagina.desde}
                que="pallets"
                onIr={pagina.setPagina}
              />
            </div>
          </>
        )
      )}
      {dialogo}
    </div>
  );
}

/** El producto principal y, si hay más, «+N» que despliega todos los renglones. */
function Productos({ e }: { e: PalletEliminado }) {
  const [primero, ...resto] = e.items;
  const renglon = (i: PalletEliminado['items'][number]) =>
    [i.descripcion, i.numeroParte && `N° parte ${i.numeroParte}`, i.lote && `lote ${i.lote}`, `${miles(i.piezas)} pz`]
      .filter(Boolean)
      .join(' · ');
  if (!primero) return <span style={{ fontFamily: mono, fontSize: 13 }}>{e.articulo ?? '—'}</span>;
  return (
    <details style={{ fontSize: 13 }}>
      <summary style={{ cursor: resto.length ? 'pointer' : 'default', listStyle: resto.length ? undefined : 'none' }}>
        <span style={{ fontFamily: mono }}>{primero.articulo}</span>
        {resto.length > 0 && <span style={{ color: c.texto3 }}> +{resto.length}</span>}
      </summary>
      {e.items.map((i, n) => (
        <div key={n} style={{ marginTop: 4, fontSize: 12, color: c.texto2, textWrap: 'pretty' }}>
          <span style={{ fontFamily: mono, color: c.texto }}>{i.articulo}</span> · {renglon(i)}
        </div>
      ))}
    </details>
  );
}

/** Un eliminado en tarjeta: la lista en el celular y la ficha del contenedor. */
export function TarjetaEliminado({
  e,
  onVerContenedor,
  onQuitarDeclarado,
  indice,
  onRestablecer,
}: {
  e: PalletEliminado;
  onVerContenedor?: (ct: string) => void;
  /** Sólo donde se puede (la ficha, administración): se ofrece mientras sus piezas sigan pendientes. */
  onQuitarDeclarado?: () => void;
  /** Su lugar en la lista, para que entren escalonadas; sin él (la ficha) no se anima. */
  indice?: number;
  /** Volverlo a poner en el sistema; sólo administración, y si le quedaba algo que restablecer. */
  onRestablecer?: () => void;
}) {
  const dato = (rotulo: string, valor: ReactNode) => (
    <>
      <span style={{ color: c.texto3 }}>{rotulo}</span>
      <span style={{ color: c.texto, minWidth: 0, overflowWrap: 'anywhere' }}>{valor}</span>
    </>
  );
  return (
    <motion.div
      {...(indice !== undefined ? { layout: 'position' as const, ...fila(indice) } : {})}
      style={{ padding: '14px 16px', borderBottom: `1px solid ${c.borde}`, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 600 }}>{e.pallet}</span>
        <span style={{ fontSize: 12, color: c.texto3 }}>
          {cuando(e.eliminadoEn)} · {e.eliminadoPor}
        </span>
      </div>
      <Productos e={e} />
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 12px', fontSize: 13 }}>
        {dato('Contenedor', <Contenedor e={e} onVer={onVerContenedor} />)}
        {dato('Piezas', e.piezas != null ? miles(e.piezas) : '—')}
        {dato('Ubicación', ubicacion(e))}
        {dato('Estado', [e.estado, e.tipo].filter(Boolean).join(' · ') || '—')}
        {dato('Motivo', e.motivo)}
      </div>
      {onQuitarDeclarado && !e.declaradoQuitado && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: c.texto2 }}>
          <span style={{ flex: '1 1 240px' }}>
            Sus piezas siguen pendientes de paletizar: el contenedor no pasa a descargado hasta armarle otro pallet o quitarlo de lo
            declarado.
          </span>
          <button type="button" onClick={onQuitarDeclarado} style={{ ...botonSecundario, minHeight: 40, padding: '6px 14px', fontSize: 13 }}>
            Quitar de lo declarado
          </button>
        </div>
      )}
      {onRestablecer && e.restablecible && (
        <div>
          <BotonRestablecer onClick={onRestablecer} />
        </div>
      )}
    </motion.div>
  );
}
